/**
 * modularAssessmentMapper.js
 *
 * Transforms subcollection-based formInstances (used by Adult Autism and future
 * modular modules) into the assessment object shape expected by QuestionnaireAccordion.
 *
 * The ADHD PatientDashboard builds its assessments from patientDataModel.dataModel
 * sections/domains. This mapper achieves the same output shape from the simpler
 * formInstances map, allowing the shared QuestionnaireAccordion to render both
 * ADHD and modular pathways without any modifications.
 *
 * Additionally, builds a "synthetic patientDataModel" object that satisfies the
 * two reads QuestionnaireAccordion performs on patientDataModel:
 *   1. hasCompletedInformantSetupForm (informant gate)
 *   2. formData[formKey].progress.{totalQuestions, answeredQuestions} (time estimates)
 *
 * This avoids modifying the shared component's interface while keeping ADHD
 * completely regression-free.
 */

/**
 * Count the total number of questions in a form definition.
 * Questions are nested under formSubsections > questions.
 *
 * @param {Object|null} formDefinition - The formDefinition object from a form instance
 * @returns {number} Total question count across all subsections
 */
export function countQuestionsInDefinition(formDefinition) {
  if (!formDefinition?.formSubsections) return 0;

  let total = 0;
  Object.values(formDefinition.formSubsections).forEach(subsection => {
    if (subsection.questions) {
      total += Object.keys(subsection.questions).length;
    }
  });
  return total;
}

/**
 * Convert formInstances into the flat assessment objects that QuestionnaireAccordion
 * renders. Each form instance becomes one assessment item (leaf node, not grouped).
 *
 * @param {Object} formInstances - Map of formKey -> formInstance from context
 * @param {string} routePrefix - Base route path, e.g. '/service-user/form'
 * @returns {Array} Sorted assessment objects matching QuestionnaireAccordion's shape
 */
export function mapFormInstancesToAssessments(formInstances, routePrefix = '/service-user/form') {
  if (!formInstances || typeof formInstances !== 'object') return [];

  return Object.entries(formInstances)
    .filter(([, fi]) => fi && fi.formDefinition)
    .map(([formKey, fi]) => ({
      title: fi.formTitle || formKey,
      // Match ADHD dashboard: intro copy (formExplanationText) is not shown to users.
      subtitle: '',
      route: `${routePrefix}/${formKey}`,
      formKey,
      progress: Math.round(Math.min(1, Math.max(0, fi.progress || 0)) * 100),
      type: 'history',
      suggestedOrder: fi.formNumber || 0,
      isGroup: false,
    }))
    .sort((a, b) => a.suggestedOrder - b.suggestedOrder);
}

/**
 * Compute the index of the next incomplete assessment in the sorted list.
 * Returns -1 if all are complete (used by QuestionnaireAccordion to suppress
 * the "next up" violet pulse when everything is done).
 *
 * @param {Array} assessments - Sorted assessment objects from mapFormInstancesToAssessments
 * @returns {number} Index of first assessment with progress < 100, or -1 if all complete
 */
export function computeNextQuestionnaireIndex(assessments) {
  if (!assessments || assessments.length === 0) return -1;

  const idx = assessments.findIndex(a => a.progress < 100);
  return idx >= 0 ? idx : -1;
}

/**
 * Build a synthetic patientDataModel object that satisfies QuestionnaireAccordion's
 * two property reads without requiring any changes to the shared component:
 *
 *   1. patientDataModel.hasCompletedInformantSetupForm -> informant setup gate
 *   2. patientDataModel.formData[formKey].progress.{totalQuestions, answeredQuestions}
 *      -> time estimate calculations
 *
 * The accordion uses these to compute "est. X mins" for each form item and to
 * decide whether to show the informant setup card blocking access to forms.
 *
 * @param {Object|null} header - The header doc from ModularPatientSelfDataContext
 * @param {Object} formInstances - Map of formKey -> formInstance
 * @returns {Object} Object shaped like the subset of patientDataModel the accordion reads
 */
export function buildSyntheticPatientDataModel(header, formInstances) {
  const formData = {};

  if (formInstances && typeof formInstances === 'object') {
    Object.entries(formInstances).forEach(([formKey, fi]) => {
      const totalQuestions = countQuestionsInDefinition(fi?.formDefinition);
      const progress = Math.min(1, Math.max(0, fi?.progress || 0));
      const answeredQuestions = Math.round(progress * totalQuestions);

      formData[formKey] = {
        progress: {
          progressPercentage: Math.round(progress * 100),
          totalQuestions,
          answeredQuestions,
        },
      };
    });
  }

  const linkedPatientFirstName = header?.patientFirstName?.trim() || null;

  return {
    hasCompletedInformantSetupForm: header?.hasCompletedInformantSetupForm || false,
    assessmentModule: header?.assessmentModule || 'adultAutism',
    formData,
    // Service user's own first name (patient or informant) for welcome copy
    firstName: header?.firstName || null,
    // Linked patient's first name — set on informant headers from activeAssignments
    patientFirstName: linkedPatientFirstName,
    // Informant dashboard copy: prefer linked patient name, not the informant's own name
    createdByName: linkedPatientFirstName
      || (header?.firstName
        ? `${header.firstName}${header.lastName ? ` ${header.lastName}` : ''}`
        : null),
  };
}

// ─── Dashboard Form Groupings ─────────────────────────────────────────────────

/**
 * Static form-key-to-group map for the Autism patient dashboard. Groups mirror
 * ADHD's thematic structure. Using fixed keys rather than keyword matching because
 * Autism form keys are deterministic (defined in adultAutismFormKeys.js).
 */
export const AUTISM_PATIENT_FORM_GROUPS = [
  { label: 'About You', keys: ['yourInformation', 'yourCurrentConcerns'] },
  { label: 'Health and Wellbeing', keys: ['yourWellbeing', 'yourHealth', 'yourFamily'] },
  { label: 'Childhood', keys: ['yourChildhood'] },
  { label: 'Adult Life', keys: ['yourAdultLife', 'yourLifestyle', 'youAndTheLaw'] },
  { label: 'Sensory Experiences', keys: ['sensoryExperiences'] },
];

/**
 * Third-person equivalents for the informant dashboard. Informants only have
 * 7 forms (no yourInformation or yourCurrentConcerns).
 */
export const AUTISM_INFORMANT_FORM_GROUPS = [
  { label: 'Their Health and Wellbeing', keys: ['yourWellbeing', 'yourHealth', 'yourFamily'] },
  { label: 'Their Childhood', keys: ['yourChildhood'] },
  { label: 'Their Adult Life', keys: ['yourAdultLife', 'yourLifestyle', 'youAndTheLaw'] },
];

/**
 * Group flat autism assessments into accordion sections. Forms matching a group's
 * keys become children; unmatched forms render individually at the end.
 *
 * Each group's progress is the average of its children's progress values, which
 * works correctly with computeNextQuestionnaireIndex (a group with any incomplete
 * child will have progress < 100).
 *
 * @param {Array} assessments - Flat assessments from mapFormInstancesToAssessments
 * @param {Array} groupDefinitions - Array of { label, keys } objects
 * @returns {Array} Mixed array of group objects (isGroup: true) and ungrouped leaf objects
 */
export function groupAutismAssessments(assessments, groupDefinitions) {
  const grouped = [];
  const claimed = new Set();

  groupDefinitions.forEach(({ label, keys }) => {
    // Preserve the order defined in keys, not the suggestedOrder
    const children = keys
      .map(key => assessments.find(a => a.formKey === key))
      .filter(Boolean);

    if (children.length === 0) return;

    children.forEach(c => claimed.add(c.formKey));

    const totalProgress = children.reduce((sum, c) => sum + c.progress, 0);
    grouped.push({
      isGroup: true,
      title: label,
      progress: Math.round(totalProgress / children.length),
      type: 'history-group',
      formKey: label.toLowerCase().replace(/\s+/g, '-'),
      route: null,
      children,
    });
  });

  // Append any assessments not claimed by a group (e.g. org-specific extras)
  assessments.forEach(a => {
    if (!claimed.has(a.formKey)) {
      grouped.push(a);
    }
  });

  return grouped;
}
